<script lang="ts">
  import { categories } from "$lib/stores/categories.svelte";
  import { accounts } from "$lib/stores/accounts.svelte";

  export interface ConfirmItem {
    _id: number;
    selected: boolean;
    kind: "expense" | "income";
    amount: string;
    description: string;
    due_date: string;
    installments: number;
    category_id: string | null;
    confidence: number;
  }

  let {
    items           = $bindable<ConfirmItem[]>([]),
    sharedAccountId = $bindable<string | null>(null),
    selectedCount,
    selectedTotal,
    onSave,
    onClose,
  } = $props<{
    items:           ConfirmItem[];
    sharedAccountId: string | null;
    selectedCount:   number;
    selectedTotal:   number;
    onSave:  () => void;
    onClose: () => void;
  }>();

  function toggleItemKind(item: ConfirmItem) {
    item.kind = item.kind === "expense" ? "income" : "expense";
    item.category_id = null;
  }

  function toggleAll(select: boolean) {
    for (const item of items) item.selected = select;
  }
</script>

<!-- Header fixo -->
<div class="sticky top-0 z-10 px-4 pt-4 pb-3 flex items-center justify-between gap-2"
     style="background: var(--surface); border-bottom: 1px solid var(--border);">
  <div>
    <h2 class="text-base font-bold leading-tight">
      {items.length} lançamento{items.length !== 1 ? "s" : ""} encontrado{items.length !== 1 ? "s" : ""}
    </h2>
    <p class="text-xs mt-0.5" style="color: var(--text-muted);">
      Revise, corrija e salve os que quiser.
    </p>
  </div>
  <button type="button" class="btn-ghost text-base leading-none -mr-1" onclick={onClose} aria-label="Fechar">✕</button>
</div>

<div class="px-4 py-3 flex flex-col gap-3">

  <!-- Conta compartilhada -->
  <div>
    <label class="label text-xs" for="scan-acc">Conta para todos os lançamentos</label>
    {#if accounts.list.length === 0}
      <p class="text-sm" style="color: var(--text-muted);">Carregando contas…</p>
    {:else}
      <select id="scan-acc" class="input" bind:value={sharedAccountId}>
        <option value={null}>— Selecione a conta —</option>
        {#each accounts.list as acc}
          <option value={acc.id}>{acc.icon ?? ""} {acc.name}</option>
        {/each}
      </select>
    {/if}
  </div>

  <!-- Selecionar / desselecionar tudo -->
  <div class="flex items-center justify-between text-xs" style="color: var(--text-muted);">
    <span>{selectedCount} de {items.length} selecionado{selectedCount !== 1 ? "s" : ""}</span>
    <div class="flex gap-3">
      <button type="button" class="underline" onclick={() => toggleAll(true)}>Selecionar todos</button>
      <button type="button" class="underline" onclick={() => toggleAll(false)}>Desmarcar todos</button>
    </div>
  </div>

  <!-- Lista de itens -->
  <div class="flex flex-col gap-2">
    {#each items as item (item._id)}
      {@const lowConf = item.confidence < 0.5}
      <div
        class="rounded-xl p-3 flex flex-col gap-2"
        style="background: var(--surface-alt); border: 1.5px solid {item.selected ? (lowConf ? '#fcd34d' : 'var(--brand)') : 'var(--border)'}; opacity: {item.selected ? 1 : 0.55};"
      >
        <!-- Row 1: checkbox + tipo + descrição -->
        <div class="flex items-center gap-2">
          <input
            type="checkbox"
            class="w-4 h-4 flex-shrink-0 accent-brand"
            bind:checked={item.selected}
            aria-label="Selecionar lançamento"
          />
          <button
            type="button"
            class="flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full transition-colors"
            style="background: {item.kind === 'expense' ? '#fee2e2' : '#d1fae5'}; color: {item.kind === 'expense' ? '#b91c1c' : '#065f46'};"
            onclick={() => toggleItemKind(item)}
            title="Clique para alternar Despesa/Receita"
          >
            {item.kind === "expense" ? "💸 Desp." : "💰 Rec."}
          </button>
          <input
            type="text"
            class="input flex-1 text-sm py-1 min-w-0"
            placeholder="Descrição"
            bind:value={item.description}
            maxlength="60"
          />
          {#if lowConf}
            <span title="IA com baixa confiança — revise este item" class="text-amber-500 flex-shrink-0" aria-label="Baixa confiança">⚠️</span>
          {/if}
        </div>

        <!-- Row 2: valor + vencimento + parcelas + categoria -->
        <div class="flex gap-2 items-center pl-6 flex-wrap">
          <input
            type="text"
            class="input text-sm py-1 font-semibold"
            style="width: 90px; flex-shrink: 0;"
            inputmode="decimal"
            placeholder="0,00"
            bind:value={item.amount}
          />
          <div class="flex flex-col" style="flex: 1; min-width: 110px;">
            <span class="text-xs mb-0.5" style="color: var(--text-muted);">Vencimento</span>
            <input type="date" class="input text-sm py-1" bind:value={item.due_date} />
          </div>
          <div class="flex flex-col" style="flex: 1; min-width: 90px;">
            <span class="text-xs mb-0.5" style="color: var(--text-muted);">Parcelas</span>
            <select class="input text-sm py-1" bind:value={item.installments}>
              <option value={1}>1× à vista</option>
              {#each [2,3,4,5,6,7,8,9,10,11,12] as n}
                <option value={n}>{n}×</option>
              {/each}
            </select>
          </div>
          <select
            class="input text-sm py-1"
            style="flex: 2; min-width: 100px;"
            bind:value={item.category_id}
          >
            <option value={null}>Categ.</option>
            {#each categories.byKind(item.kind) as cat}
              <option value={cat.id}>{cat.name}</option>
            {/each}
          </select>
        </div>
      </div>
    {/each}
  </div>

  <!-- Barra de resumo + botão salvar -->
  <div class="sticky bottom-0 -mx-4 px-4 pt-3 pb-4" style="background: var(--surface); border-top: 1px solid var(--border);">
    {#if selectedCount > 0}
      <div class="flex items-center justify-between text-sm mb-2">
        <span class="font-medium">{selectedCount} lançamento{selectedCount !== 1 ? "s" : ""}</span>
        <span class="font-bold text-base">{selectedTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span>
      </div>
      <p class="text-xs mb-2" style="color: var(--text-muted);">
        🔔 Todos salvos como <strong>Pendente</strong> — marque como pago quando efetuar o pagamento
      </p>
    {/if}
    <button
      type="button"
      class="btn-primary w-full py-3 text-base"
      disabled={selectedCount === 0 || !sharedAccountId}
      onclick={onSave}
    >
      {#if selectedCount === 0}
        Selecione ao menos 1 lançamento
      {:else if !sharedAccountId}
        Selecione uma conta acima
      {:else}
        ✅ Salvar {selectedCount} lançamento{selectedCount !== 1 ? "s" : ""}
      {/if}
    </button>
    <button type="button" class="btn-ghost text-sm w-full mt-2 text-center" onclick={onClose}>
      Cancelar
    </button>
  </div>
</div>
